import { WorkItemPool } from 'common/workitems/workitem-pool';
import { bcAuthEvents } from 'common/common/constants/auth-events';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ServiceEvaluatorService } from 'shared/services/service-evaluator/service-evaluator.service';
import { AccountVersionInfoModel } from '../api/models/account-version-info.model';
import { CurrentUserModel } from '../api/models/current-user.model';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { bcStates } from 'common/common/states';
import { IdentityLoginConfigurationModel } from 'common/api/models/identity-login-configuration.model';
import { BcRestApi } from 'common/api/rest-api';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Injectable, NgZone } from '@angular/core';
import { CoolLocalStorage, CoolSessionStorage } from '@angular-cool/storage';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { WindowService } from 'shared/services/window/window.service';
import { DynamicQueryManagerService } from 'common/dynamicquery/dynamic-query-manager.service';
import { BcConfigService } from 'common/config/provider';
import { CookieService } from 'ngx-cookie-service';
import { CompatibilityService } from 'common/compatibility/compatibility.service';
import { LoggerService } from 'common/logger/logger.service';
import { LogService } from 'common/logger/log.service';
import { AuthenticationType } from 'common/auth/models/authentication-type';
import { detect } from 'detect-browser';
import { CsrfTokenService } from 'common/api/csrf-token/csrf-token.service';

const sha256 = require('hash.js/lib/hash/sha/256'); // eslint-disable-line import/no-internal-modules

@Injectable({
  providedIn: 'root'
})
export class BcAuth {
  private readonly validUrlMatcher: RegExp = new RegExp(/^(thundercat|admin|assistant-touchpoints)-?(dev|gamma|demo|preview|local|alpha[a-z]{2}\d)?\.?(satellite\.)?(bold360|boldchat)\.(com|io)(:\d{4})?$/);
  private readonly boldVersionCookieName = 'bold-version';
  private readonly detectedBrowser;

  public storageKey;
  public storageDraftKey;
  // TODO: remove workItem dependency
  public WorkItemPool;
  public isSSO: boolean;
  private ssoAccountId: string;
  private identityLoginConfiguration: Promise<IdentityLoginConfigurationModel> = null;
  private _logger: LoggerService;
  readonly preferredLanguageKey = 'bold360PreferredLanguage';
  readonly disableCommonLoginKey = 'disableCommonLogin';

  constructor(private bcRestApi: BcRestApi,
              private sessionStorageService: CoolSessionStorage,
              private _dynamicQueryManagerService: DynamicQueryManagerService,
              private bcNotificationService: NotificationBarService,
              private bcAnalytics: BcAnalytics,
              private userContextService: UserContextService,
              private bcSettingsService: BcSettingsService,
              private bcConfig: BcConfigService,
              private userPermissionService: UserPermissionService,
              private $state: StateServiceWrapper,
              private serviceEvaluator: ServiceEvaluatorService,
              private $cookies: CookieService,
              private $window: WindowService,
              private ngZone: NgZone,
              private RxEmitter: RxEmitter,
              private compatibilityService: CompatibilityService,
              private _logService: LogService,
              private localStorageService: CoolLocalStorage,
              private _csrfTokenService: CsrfTokenService) {

    this._logger = this._logService.getLoggerInstance(BcAuth.name);
    this.compatibilityService.ensureUpgrade();

    this.storageKey = bcSessionKeys.storage.key;
    this.storageDraftKey = bcSessionKeys.storage.drafts;
    this.WorkItemPool = WorkItemPool.getInstance();
    this.isSSO = false;
    this.ssoAccountId = null;
    this.detectedBrowser = detect();

    this.RxEmitter.subscribe('websocket-error', () => {
      // Validate login through a REST API request, 401 interceptor should throw us back to login page if failed
      this.getCurrentUser().then((user) => this.setSession(user));
    });

    this._initRestApi();
  }

  initForUser(params) {
    return this.bcRestApi.initApiForUser(params);
  }

  getSession() {
    const session = this.sessionStorageService.getItem(this.storageKey);
    if (session) {
      const parsedSessionInfo = JSON.parse(session);
      this.userContextService.setUserContext(parsedSessionInfo);
      return parsedSessionInfo;
    }
    return null;
  }

  setSession(sessionData) {
    this.sessionStorageService.setItem(this.storageKey, JSON.stringify(sessionData));
  }

  clearSession() {
    this.sessionStorageService.removeItem(this.storageKey);
    this.sessionStorageService.removeItem(this.storageDraftKey);
  }

  getSessionUserName(): string {
    const session = this.getSession();
    return session && session.name;
  }

  getSessionUserId() {
    const session = this.getSession();
    return session && session.id;
  }

  getSessionAccountId(): string {
    const session = this.getSession();
    return session && session.accountId;
  }

  isLoggedIn(): boolean {
    return !!this.getSession();
  }

  isAuthenticated(): Promise<boolean> {
    return this.bcRestApi.isAuthenticated();
  }

  getCurrentUser(): Promise<CurrentUserModel> {
    return this.bcRestApi.getCurrentUser();
  }

  setWebSocketEndpoint(): Promise<void> {
    if (!this.bcConfig.current.features.geoAffinity) {
      this.bcConfig.setWebSocketEndpointFromLocalConfig();
      return Promise.resolve();
    }

    return this.bcRestApi.getWebSocketEndpoint()
      .then(response => {
        this.bcConfig.setWebSocketEndpoint(response.url);
      });
  }

  async prepareFrameworkAfterLogin(authenticationType: AuthenticationType): Promise<boolean> {
    const user = await this.getCurrentUser();
    this.setCurrentUserContext(user);

    await this.setWebSocketEndpoint();
    const userIdHash = this._getHash(user.id);
    const accountIdHash = this._getHash(user.accountId);
    const currentLanguage = this.getCurrentLanguage();
    await this.userPermissionService.getPermissionValues();
    if (authenticationType !== AuthenticationType.RECONNECT) {
      this.bcAnalytics.setUserId(userIdHash);
      this.bcAnalytics.updateUserProperties({
        accountId: accountIdHash,
        screenDimensions: { width: screen.width, height: screen.height },
        windowDimension: { width: window.innerWidth, height: window.innerHeight }
      });
      this.bcAnalytics.eventTrack('LoginSuccess', {
        userIdHash: userIdHash,
        language: currentLanguage
      });
    }

    // Fetch/cache settings
    this.bcSettingsService.resetSettings();
    await this.bcSettingsService.getSettings();

    this.serviceEvaluator.setServiceAccess(user);
    await this.serviceEvaluator.getServices();
    const shouldReload = await this.shouldRedirectToAppropriateAgentVersion();
    return !shouldReload;
  }

  private setCurrentUserContext(user: CurrentUserModel): void {
    this.userContextService.setUserContext(user);
    this.setSession(user);
  }

  public async processReturnUrl(accountId: string): Promise<string> {
    const returnLocation = this.getReturnLocation(accountId);
    if (returnLocation) {
      await this._requestSetupToken();
      return returnLocation;
    }
    return null;
  }

  async shouldRedirectToAppropriateAgentVersion(): Promise<boolean> {
    const accountVersionInfo: AccountVersionInfoModel = await this.bcRestApi.getAccountVersion();
    let accountVersion = accountVersionInfo.frontendVersion;
    if (!accountVersion) {
      accountVersion = this._getVersionInFrontendFormat(accountVersionInfo.version);
    }
    const currentAgentVersion = this.$cookies.get(this.boldVersionCookieName);

    if (accountVersion !== currentAgentVersion) {
      const currentDate = new Date();
      const expirationDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
      this.$cookies.set(this.boldVersionCookieName, accountVersion, expirationDate, '/');
      return true;
    }
    return false;
  }

  public reloadAfterLoginIfNeeded() {
    // Workaround for issue BLD-55229
    if (this.isSafari()) {
      this.$cookies.delete(this.boldVersionCookieName, '/');
    }
  }

  async login(loginData, rememberMe: boolean): Promise<boolean> {
    const isLoggedIn = this.isLoggedIn();

    this._clearLoginErrorNotifications();

    if (!isLoggedIn) {
      // Replace stored RestApiHost on every new login to use the values from the config file
      this.bcConfig.setRestApiHostFromLocalConfig();
      try {
        await this.bcRestApi.initApiForUser({ username: loginData.username });
        await this.bcRestApi.login(loginData, rememberMe);
        const user = await this.getCurrentUser();
        const returnUrl = await this.processReturnUrl(user.accountId);
        if (returnUrl) {
          this.$window.location.href = returnUrl;
        }
      } catch (response) {
        this.bcAnalytics.eventTrack('LoginError', { status: response.status });
        return Promise.reject('Error while trying to log in. Response: ' + response);
      }
    }

    return Promise.resolve(isLoggedIn);
  }

  isIdentityLoginAllowed(routeSnapshot?: ActivatedRouteSnapshot): boolean {
    const disableCommonLoginCookie: string = this.$cookies.get(this.disableCommonLoginKey);
    let disableCommonLogin: boolean = disableCommonLoginCookie === 'true';

    if (!disableCommonLogin && routeSnapshot) {
      disableCommonLogin = this.persistDisableCommonLoginParameter(routeSnapshot);
    }

    return !disableCommonLogin;
  }

  persistDisableCommonLoginParameter(routeSnapshot: ActivatedRouteSnapshot): boolean {
    let disableCommonLogin = routeSnapshot.queryParams && routeSnapshot.queryParams.disableCommonLogin === 'true';
    if (disableCommonLogin) {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 1);
      this.$cookies.set('disableCommonLogin', `${disableCommonLogin}`, expirationDate, '/', this.bcConfig.current.cookieDomain);
    }
    return disableCommonLogin;
  }

  async getIdentityLoginConfiguration(): Promise<IdentityLoginConfigurationModel> {
    if (this.identityLoginConfiguration != null) {
      return this.identityLoginConfiguration;
    }

    if (this._csrfTokenService.needsCsrfTokenRequest) {
      const csrfTokenResponse = await this.bcRestApi.getCsrfToken();
      this._csrfTokenService.set(csrfTokenResponse?.csrfToken);
    }

    const location = this.$window.location;
    let redirectUrl = location.origin;
    redirectUrl = redirectUrl ? redirectUrl : 'https://agent.bold360.com';  // Safety fallback for live environment!
    if (!this.bcConfig.current.isBoldDomain) {
      const urlPieces = [location.protocol, '//', location.host, location.pathname]
      redirectUrl = urlPieces.join('');
    }
    const baseRedirectPath = '/?account-id=@aid@';
    const returnParam = this._getSearchParam('return');
    const redirectPath = returnParam ? `${baseRedirectPath}&return=${returnParam}` : baseRedirectPath;
    this.identityLoginConfiguration = this.bcRestApi.getIdentityLoginConfiguration(redirectUrl, redirectPath, redirectPath)
      .catch(() => {
        const identityLoginConfiguration: IdentityLoginConfigurationModel = {
          enabled: false,
          loginRedirectUrl: '',
          logoutRedirectUrl: '',
          changePasswordUrl: '',
          genesysCloudOauthRedirectUrl: ''
        };
        return identityLoginConfiguration;
      });
    return this.identityLoginConfiguration;
  }

  private _getSearchParam(paramName) {
    if (!this.$window.location.search) {
      return;
    }
    const foundParam = this.$window.location.search.slice(1).split('&')
      .find(param => param.indexOf(`${paramName}=`) === 0);
    return foundParam && foundParam.substr(`${paramName}=`.length);
  }

  async validateIdentityTicket(accountId: string, identityTicket: string): Promise<void> {
    this._clearLoginErrorNotifications();
    this.bcRestApi.setAccountId(accountId);
    const { csrfToken } = await this.bcRestApi.validateIdentityTicket(identityTicket);
    this._csrfTokenService.set(csrfToken);
  }

  async logout(reason?: string | { token: string }, returnUrl?: string): Promise<void> {
    if (this.isLoggedIn()) {
      try {
        await this.logoutWorker(reason);
      } catch (e) {
        /* logout failed, still should navigate to login page */
      }
      return this.navigateToLogin(returnUrl);
    }

    try {
      await this.initForUser(null);
      const isAuthenticated = await this.isAuthenticated();
      if (isAuthenticated) {
        await this.logoutWorker(reason);
      }
    } catch (e) {
      /* logout failed, still should navigate to login page */
    }
    return this.navigateToLogin(returnUrl);
  }

  async switchAccount(): Promise<void> {
    if (this.isLoggedIn()) {
      try {
        await this.logoutWorker('Switch Account');
      } catch (e) {
        this._logger.logError('Switch Account Failed: {}', e);
      }

      const identityLoginConfig = await this.getIdentityLoginConfiguration();
      this.$window.location.href = identityLoginConfig.loginRedirectUrl;
    }
  }

  logoutWorker(reason?: string | { token: string }): Promise<void> {
    this.bcAnalytics.eventTrack('Logout', { trace: JSON.stringify(new Error().stack), reason: JSON.stringify(reason) });

    return this._dynamicQueryManagerService.close()
      .finally(() => this.getIdentityLoginConfiguration())
      .finally(() => this.bcRestApi.logout())
      .finally(() => {
        // Clear notifications
        this.bcNotificationService.removeNotification('permission-error');
        this.bcNotificationService.removeNotification('server-error');

        // Manual clean up in services
        this.clearSession();
        this.WorkItemPool.resetPool();
        this._initAccountAwareRestApi(null);

        // Notify services to clean up themselves
        this.RxEmitter.send(bcAuthEvents.logoutSuccess, null);
      });
  }

  async navigateToLogin(returnUrl: string): Promise<void> {
    if (this.isIdentityLoginAllowed()) {
      const identityLoginConfiguration: IdentityLoginConfigurationModel = await this.getIdentityLoginConfiguration();
      if (identityLoginConfiguration.enabled && !this.isSSO) {
        this.$window.location.href = identityLoginConfiguration.logoutRedirectUrl;
        return;
      }
    }

    let queryParams: any = {};
    if (returnUrl) {
      queryParams.return = returnUrl;
    }
    if (this.isSSO && this.ssoAccountId) {
      queryParams.aid = this.ssoAccountId;
    }

    return this.ngZone.run(() => {
      this.$state.go(bcStates.login.url, {}, { queryParams: queryParams, queryParamsHandling: 'merge' });
    });
  }

  setSSO(isSSO: boolean) {
    this.isSSO = isSSO;
    this.ssoAccountId = isSSO ? this.bcRestApi.getAccountId() : null;
  }

  private getReturnLocation(accountId: string): string | null {
    let returnValue = this._getSearchParam('return');
    if (returnValue !== undefined && this.isRedirectUrlValid(returnValue)) {
      const serverSetDomain = this.bcConfig.serverSetDomain || '';
      return `https://${returnValue}/return/${serverSetDomain}?serverSetDomain=${serverSetDomain}&accountId=${accountId}`;
    }
    return null;
  }

  isRedirectUrlValid(url: string) {
    return this.validUrlMatcher.test(url);
  }

  public getCurrentLanguage(): string {
    const languageCode: undefined | string = this.localStorageService.getItem(this.preferredLanguageKey);
    let selectedLanguage = this.bcConfig.current.languages.find(lang => lang.key === languageCode);

    if (!selectedLanguage) {
      selectedLanguage = this.bcConfig.current.languages.reduce((accu, actual) => accu && accu.priority < actual.priority ? accu : actual);
    }

    return selectedLanguage.name;
  }

  public async getUserAccountCount(): Promise<number> {
    let userAccountCount: number = 1;
    const identityLoginConfig = await this.getIdentityLoginConfiguration();

    if (identityLoginConfig && identityLoginConfig.enabled) {
      userAccountCount = (await this.bcRestApi.getUserAccountCount()).accountCount;
    }

    return userAccountCount;
  }

  private _getHash(stringToHash: string): string {
    return sha256().update(stringToHash).digest('hex');
  }

  private _getVersionInFrontendFormat(version: string): string {
    return 'v' + version.split('.').splice(0, 3).join('_');
  }

  private _requestSetupToken(): Promise<void> {
    return this.bcRestApi.getSetupToken();
  }

  private _clearLoginErrorNotifications() {
    // Clear notifications from earlier
    this.bcNotificationService.removeNotification('logged-out');
    this.bcNotificationService.removeNotification('permission-error');
    this.bcNotificationService.removeNotification('server-error');
    this.bcNotificationService.removeNotification('sso-error');
  }

  private _initRestApi(): void {
    const accountId: string = this.getSessionAccountId();
    this._initAccountAwareRestApi(accountId);
  }

  private _initAccountAwareRestApi(accountId: string): void {
    if (accountId) {
      this.bcRestApi.setAccountId(accountId);
    } else {
      this.bcRestApi.resetAccountId();
    }
  }

  private isSafari(): boolean {
    return this.detectedBrowser && this.detectedBrowser.name === 'safari';
  }
}
